/**
 * @module middleware/Api
 */
import isError from 'lodash/isError';
import isUndefined from 'lodash/isUndefined';

import * as Actions from '../actions';
import { ApiResponseTypes, ApiToken } from '../constants/ApiActions';
import Api from '../lib/Api';
import { getActionPrefix, getActionTypesForPrefix, getSchemaByEndpoint } from '../lib/ApiUtils';
import ApiError, { ApiErrorTypes } from '../lib/error/ApiError';

const METHODS = ['get', 'post'];

// A Redux middleware that interprets actions with ApiToken info specified.
// Performs the call and promises when such actions are dispatched.
export default ({ dispatch, getState }) =>
  next =>
  action => {
    const callAPI = action[ApiToken];
    if (isUndefined(callAPI)) {
      return next(action);
    }
    let { endpoint, serviceEndpoint, callback } = callAPI;
    const {
      query,
      serviceHostType,
      content = {},
      authToken = null,
      params = {},
      method = 'post',
      sync = false,
      adapter,
      parser,
      responseType = ApiResponseTypes.JSON,
    } = callAPI;
    const requestHeaders = callAPI.headers;
    const schema = getSchemaByEndpoint(endpoint || serviceEndpoint);
    const actionPrefix = getActionPrefix(endpoint || serviceEndpoint);
    const [requestType, successType, failureType] = getActionTypesForPrefix(actionPrefix);

    if (typeof endpoint === 'function') {
      endpoint = endpoint();
    }

    if (typeof serviceEndpoint === 'function') {
      serviceEndpoint = serviceEndpoint();
    }

    if (typeof endpoint !== 'string' && typeof serviceEndpoint !== 'string') {
      throw new Error('Specify a string endpoint URL.');
    }

    if (METHODS.indexOf(method) === -1) {
      throw new Error('Methods supported are "get" and "post"');
    }

    if (!schema) {
      throw new Error('Specify one of the exported Schemas.');
    }

    if (!callback) {
      callback = () => false;
    }

    if (typeof query === 'string') {
      endpoint = `${endpoint}${query}`;
    }

    function actionWith(data) {
      const request = { content, query, params };
      const finalAction = Object.assign({ request }, action, data);
      // We want to pass along anything that was in initial action,
      // not part of the api call
      delete finalAction[ApiToken];
      return finalAction;
    }

    next(actionWith({ type: requestType }));

    const headers = Object.assign({}, requestHeaders || {});

    return Api.makeApiRequest({
      serviceEndpoint,
      serviceHostType,
      endpoint,
      content,
      params,
      schema,
      method,
      adapter,
      sync,
      parser,
      authToken,
      headers,
      responseType,
      action,
    })
      .then(response => {
        next(
          actionWith({
            response,
            type: successType,
            params,
            ...callAPI.actionParams,
          }),
        );
        callback(response);

        return Promise.resolve(response);
      })
      .catch(errorResponse => {
        // if not generic error, construct an ApiError
        const error = isError(errorResponse) ? errorResponse : new ApiError(errorResponse, callAPI);

        if (error instanceof ApiError && error.errorType === ApiErrorTypes.READONLY_MODE) {
          next(Actions.Modal.openModalReadOnlyMode());
        } else {
          next(
            actionWith({
              type: failureType,
              error,
            }),
          );
        }

        return Promise.reject(error);
      });
  };
